// Start the free plan's 7 day clock when a user first puts a site online.
//
// The gate counts 7 days from firstProjectDate, else signUpDate. Nothing on the
// Aero side ever wrote firstProjectDate, so someone who signed up and published
// a week later got a site that was already offline. This sets it, once, on the
// first publish. One copy for both runtimes: the browser (Aero create flows,
// the HTML deploy) and the server (api/_lib/deployCore.js publishVersion, which
// covers the REST zip, MCP and signed-URL deploys). Only db.collection().doc()
// get/set is used, so it works with the compat SDK and firebase-admin alike.
//
// It never restarts a clock that already started, never gives a fresh week to
// someone whose trial was already marked used, never moves the clock for
// someone who already has another live site (they are on their sign-up date),
// and never counts a re-deploy or an edit of a site that was already live
// (callers pass wasLive).
// Best effort: a failure here must never block a publish.

/**
 * A url doc that is online, in any of the shapes the two products have written:
 * an HTML site (activeVersion), a Figma site (urls, or a viewport with a link),
 * including older Figmafolio projects that have no `type` field at all.
 */
export function isLiveSiteDoc(d) {
  if (!d || d.draft === true) return false;
  if (d.activeVersion) return true;
  if (d.urls && (d.urls.figmaDesktopUrl || d.urls.figmaMobileUrl)) return true;
  return Array.isArray(d.viewports) && d.viewports.some((v) => v && v.figmaUrl);
}

/** Pure decision, so the browser and server copies can be compared. */
export function shouldStartTrialClock({ user, otherLiveSite }) {
  if (!user) return false;
  if (user.firstProjectDate) return false;
  if (user.trialUsed === true) return false;
  if (otherLiveSite) return false;
  return true;
}

/**
 * @param {object} db     Firestore (compat SDK in the browser, firebase-admin on the server)
 * @param {string} uid     the site owner
 * @param {string} siteId  the url doc that just went live (not counted as "another")
 * @param {object} [opts]
 * @param {boolean} [opts.wasLive] the site was already live before this write
 *   (a re-deploy or an edit): not a first publish, so never starts the clock
 * @param {Date} [opts.now] injectable clock, for tests
 * @returns {Promise<boolean>} true when the clock was started now
 */
export async function startTrialClock(db, uid, siteId, { wasLive = false, now = new Date() } = {}) {
  if (wasLive) return false;
  try {
    const userRef = db.collection('user').doc(uid);
    const userSnap = await userRef.get();
    if (!userSnap.exists) return false;
    const user = userSnap.data() || {};
    // Cheap exits first: most publishes are not the first.
    if (user.firstProjectDate || user.trialUsed === true) return false;
    const urls = await userRef.collection('url').get();
    const otherLiveSite = urls.docs.some((doc) => doc.id !== siteId && isLiveSiteDoc(doc.data()));
    if (!shouldStartTrialClock({ user, otherLiveSite })) return false;
    await userRef.set({ firstProjectDate: now.toISOString() }, { merge: true });
    return true;
  } catch (err) {
    console.warn('[trialClock] could not start the trial clock (non-blocking):', err && err.message);
    return false;
  }
}
